const AGENT_URL = import.meta.env.VITE_AGENT_URL || "http://localhost:8000";

export type ProductCreationOperationStatus = "initializing" | "queued" | "running" | "succeeded" | "failed" | "canceled";
export type ProductCreationOperationPhase = "validating_draft" | "provisioning_product" | "registering_build" | "processing";

export interface ProductCreationOperation {
    id: string;
    working_name: string;
    status: ProductCreationOperationStatus;
    phase: ProductCreationOperationPhase;
    project_id: number | null;
    build_id: string | null;
    retryable: boolean;
    can_return_to_draft: boolean;
    error: string | null;
    created_at: string;
    updated_at: string;
    draft_id: number;
    draft_version: number;
}

export interface LaunchProductCreationRequest {
    working_name: string;
    form_factor: string;
    draft_id: number;
    draft_version: number;
    mesh: object;
}

export class ProductCreationOperationApiError extends Error {
    constructor(
        message: string,
        readonly status: number,
        readonly code: string | null = null
    ) {
        super(message);
        this.name = "ProductCreationOperationApiError";
    }
}

const operationIdsByDraftRevision = new Map<string, string>();
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const createUuid = (): string => {
    if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, value => value.toString(16).padStart(2, "0"));
    return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
};

const operationStorageKey = (userScope: string, draftId: number, draftVersion: number): string =>
    `product-studio.product-creation-operation.${encodeURIComponent(userScope)}.${draftId}:${draftVersion}`;

export const getOrCreateProductCreationOperationId = (userScope: string, draftId: number, draftVersion: number): string => {
    const revisionKey = `${userScope}:${draftId}:${draftVersion}`;
    const storageKey = operationStorageKey(userScope, draftId, draftVersion);
    try {
        const stored = localStorage.getItem(storageKey);
        if (stored && UUID_PATTERN.test(stored)) {
            operationIdsByDraftRevision.set(revisionKey, stored);
            return stored;
        }
    } catch {
        // In-memory stability still protects double submission when storage is unavailable.
    }
    const existing = operationIdsByDraftRevision.get(revisionKey);
    if (existing) {
        try {
            localStorage.setItem(storageKey, existing);
        } catch {
            // The in-memory value remains authoritative for this session.
        }
        return existing;
    }
    const operationId = createUuid();
    operationIdsByDraftRevision.set(revisionKey, operationId);
    try {
        localStorage.setItem(storageKey, operationId);
    } catch {
        // The operation remains stable for this browser session.
    }
    return operationId;
};

/** Rotate idempotency only after the durable operation is known to be terminal. */
export const clearProductCreationOperationId = (userScope: string, draftId: number, draftVersion: number): void => {
    const revisionKey = `${userScope}:${draftId}:${draftVersion}`;
    operationIdsByDraftRevision.delete(revisionKey);
    try {
        localStorage.removeItem(operationStorageKey(userScope, draftId, draftVersion));
    } catch {
        // Storage is optional; the in-memory value has already been removed.
    }
};

const errorDetail = (payload: unknown, fallback: string): { message: string; code: string | null } => {
    if (!payload || typeof payload !== "object") return { message: fallback, code: null };
    const detail = (payload as { detail?: unknown }).detail;
    if (typeof detail === "string" && detail.trim()) return { message: detail, code: null };
    if (detail && typeof detail === "object") {
        const message = (detail as { message?: unknown }).message;
        const code = (detail as { code?: unknown }).code;
        const normalizedCode = typeof code === "string" && code.trim() ? code : null;
        if ((typeof message === "string" && message.trim()) || normalizedCode) {
            return { message: typeof message === "string" && message.trim() ? message : fallback, code: normalizedCode };
        }
    }
    const error = (payload as { error?: unknown }).error;
    if (typeof error === "string" && error.trim()) return { message: error, code: null };
    if (error && typeof error === "object") {
        const message = (error as { message?: unknown }).message;
        const code = (error as { code?: unknown }).code;
        const normalizedCode = typeof code === "string" && code.trim() ? code : null;
        if ((typeof message === "string" && message.trim()) || normalizedCode) {
            return { message: typeof message === "string" && message.trim() ? message : fallback, code: normalizedCode };
        }
    }
    return { message: fallback, code: null };
};

const readJson = async (response: Response): Promise<unknown> => response.json().catch(() => null);

const operationFromPayload = (payload: unknown): ProductCreationOperation => {
    if (payload && typeof payload === "object" && "operation" in payload) {
        const wrapper = payload as { operation: ProductCreationOperation; draft_id?: number | null };
        return wrapper.draft_id != null && wrapper.operation.draft_id == null ? { ...wrapper.operation, draft_id: wrapper.draft_id } : wrapper.operation;
    }
    return payload as ProductCreationOperation;
};

const request = async (path: string, token: string, init: RequestInit = {}): Promise<Response> =>
    fetch(`${AGENT_URL}${path}`, {
        ...init,
        credentials: "include",
        headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            ...init.headers
        }
    });

export async function launchProductCreation(token: string, operationId: string, body: LaunchProductCreationRequest): Promise<ProductCreationOperation> {
    const response = await request("/api/product-meshes/launch", token, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": operationId
        },
        body: JSON.stringify(body)
    });
    const payload = await readJson(response);
    if (response.status !== 202) {
        const detail = errorDetail(payload, `Product creation could not be launched (${response.status})`);
        throw new ProductCreationOperationApiError(detail.message, response.status, detail.code);
    }
    return operationFromPayload(payload);
}

export async function listProductCreationOperations(token: string): Promise<ProductCreationOperation[]> {
    const response = await request("/api/product-meshes?scope=dashboard", token);
    const payload = await readJson(response);
    if (!response.ok) {
        const detail = errorDetail(payload, `Failed to load product creation activity (${response.status})`);
        throw new ProductCreationOperationApiError(detail.message, response.status, detail.code);
    }
    if (Array.isArray(payload)) return payload as ProductCreationOperation[];
    if (payload && typeof payload === "object" && Array.isArray((payload as { operations?: unknown }).operations)) {
        return (payload as { operations: ProductCreationOperation[] }).operations;
    }
    return [];
}

export async function retryProductCreationOperation(token: string, operationId: string): Promise<ProductCreationOperation> {
    const response = await request(`/api/product-meshes/${encodeURIComponent(operationId)}/retry`, token, { method: "POST" });
    const payload = await readJson(response);
    if (!response.ok) {
        const detail = errorDetail(payload, `Product creation could not be retried (${response.status})`);
        throw new ProductCreationOperationApiError(detail.message, response.status, detail.code);
    }
    return operationFromPayload(payload);
}

export async function cancelProductCreationOperation(token: string, operationId: string): Promise<ProductCreationOperation> {
    const response = await request(`/api/product-meshes/${encodeURIComponent(operationId)}/cancel`, token, { method: "POST" });
    const payload = await readJson(response);
    if (!response.ok) {
        const detail = errorDetail(payload, `Product creation could not return to draft (${response.status})`);
        throw new ProductCreationOperationApiError(detail.message, response.status, detail.code);
    }
    return operationFromPayload(payload);
}

export const isActiveProductCreationOperation = (operation: ProductCreationOperation): boolean =>
    operation.status === "initializing" || operation.status === "queued" || operation.status === "running";
